import { Injectable, computed, signal, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LivefeedEvent } from '../../../shared/types/livefeed-event';
import {
  CATEGORIES_REVERSE,
  OTHER_CATEGORIES,
  DOMAIN_MAP,
  DOMAIN_AFFILIATE_MAP,
  AFFILIATE_ENABLED,
} from '../../../shared/constants';
import { DomainKey } from '../../../shared/types/product';

// Raw events from the server may include "priced" which the client remaps to "new"
interface RawLivefeedEvent extends Omit<LivefeedEvent, 'event_type'> {
  event_type: LivefeedEvent['event_type'] | 'priced';
}

interface LivefeedResponse {
  events: RawLivefeedEvent[];
  latest_id: number;
}

const MAX_EVENTS = 200;
const POLL_INTERVAL_MS = 3000;
const RATE_UPDATE_MS = 10000;

@Injectable({ providedIn: 'root' })
export class LivefeedService implements OnDestroy {
  readonly events = signal<LivefeedEvent[]>([]);
  readonly pendingEvents = signal<LivefeedEvent[]>([]);
  readonly pendingCount = computed(() => this.pendingEvents().length);
  readonly isConnected = signal(false);
  readonly isPaused = signal(false);
  readonly buffering = signal(false);

  readonly stats = signal({ total: 0, new: 0, price_change: 0, reactivated: 0 });
  readonly eventsPerMin = signal(0);

  private lastEventId = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private rateIntervalId: ReturnType<typeof setInterval> | null = null;
  private currentDomain: DomainKey = '1';
  private connectedAt = 0;
  private prevRateSnapshot = { total: 0, time: 0 };

  constructor(private http: HttpClient) {}

  connect(domain: DomainKey): void {
    this.disconnect();
    this.currentDomain = domain;
    this.lastEventId = 0;
    this.events.set([]);
    this.pendingEvents.set([]);
    this.stats.set({ total: 0, new: 0, price_change: 0, reactivated: 0 });
    this.eventsPerMin.set(0);
    this.connectedAt = Date.now();
    this.prevRateSnapshot = { total: 0, time: Date.now() };
    this.buffering.set(false);
    this.isConnected.set(true);
    this.poll();
    this.intervalId = setInterval(() => this.poll(), POLL_INTERVAL_MS);
    this.rateIntervalId = setInterval(() => this.updateRate(), RATE_UPDATE_MS);
  }

  switchDomain(domain: DomainKey): void {
    if (domain === this.currentDomain && this.isConnected()) return;
    this.connect(domain);
  }

  disconnect(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.rateIntervalId !== null) {
      clearInterval(this.rateIntervalId);
      this.rateIntervalId = null;
    }
    this.isConnected.set(false);
  }

  togglePause(): void {
    this.isPaused.update(v => !v);
  }

  flushPending(): void {
    const pending = this.pendingEvents();
    if (pending.length === 0) return;
    this.events.update(existing => {
      const combined = [...pending, ...existing];
      return combined.slice(0, MAX_EVENTS);
    });
    this.pendingEvents.set([]);
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  private poll(): void {
    const url = `api/livefeed?domain=${this.currentDomain}&after=${this.lastEventId}`;
    this.http.get<LivefeedResponse>(url).subscribe({
      next: (resp) => {
        if (resp.events.length > 0) {
          this.lastEventId = resp.latest_id;
          const enriched = resp.events.map(e =>
            this.enrichEvent(e, this.currentDomain)
          );
          this.trackStats(enriched);
          if (this.isPaused()) return;
          const newest = enriched.reverse();
          if (this.buffering()) {
            this.pendingEvents.update(p => [...newest, ...p]);
          } else {
            this.events.update(existing => {
              const combined = [...newest, ...existing];
              return combined.slice(0, MAX_EVENTS);
            });
          }
        }
      },
      error: (err) => {
        console.error('Livefeed poll error:', err);
      },
    });
  }

  private trackStats(events: LivefeedEvent[]): void {
    this.stats.update(s => {
      const updated = { ...s };
      for (const e of events) {
        updated.total++;
        if (e.event_type in updated) {
          (updated as any)[e.event_type]++;
        }
      }
      return updated;
    });
  }

  private updateRate(): void {
    const now = Date.now();
    const current = this.stats().total;
    const elapsed = (now - this.prevRateSnapshot.time) / 60000;
    if (elapsed > 0) {
      this.eventsPerMin.set(Math.round((current - this.prevRateSnapshot.total) / elapsed));
    }
    this.prevRateSnapshot = { total: current, time: now };
  }

  private enrichEvent(event: RawLivefeedEvent, domain: DomainKey): LivefeedEvent {
    const copy = { ...event } as any;
    // "priced" is an internal pipeline detail — surface it as "new"
    copy.event_type = event.event_type === 'priced' ? 'new' : event.event_type;
    if (copy.image) {
      copy.imagename = String.fromCharCode.apply('', copy.image);
    }
    if (copy.rootcat != null) {
      if (OTHER_CATEGORIES[domain]?.includes(Number(copy.rootcat))) {
        copy.category = (CATEGORIES_REVERSE[domain] as any)[1];
      } else {
        copy.category = (CATEGORIES_REVERSE[domain] as any)[copy.rootcat];
      }
    }
    if (copy.usedprice != null) {
      copy.displayusedprice = Math.max(0, copy.usedprice / 100.0);
    }
    if (copy.savingspercent != null) {
      copy.displaysavingspercent = Math.max(0, Math.floor(copy.savingspercent / 100));
    }
    if (copy.pricedifference != null) {
      copy.displaypricedifference = Math.floor(Math.max(0, copy.pricedifference / 100));
    }
    const amazonDomain = (DOMAIN_MAP as any)[domain] || 'com';
    const affiliateTag = DOMAIN_AFFILIATE_MAP[domain];
    if (AFFILIATE_ENABLED && affiliateTag) {
      copy.amazonlink = `https://www.amazon.${amazonDomain}/dp/${copy.asin}?tag=${affiliateTag}&linkCode=osi&th=1&psc=1`;
    } else {
      copy.amazonlink = `https://www.amazon.${amazonDomain}/dp/${copy.asin}?th=1&psc=1`;
    }
    return copy;
  }
}
